import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, ArrowUpRight, Inbox, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Input } from './input';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-muted', className)} />;
}

export function PageHeader({ title, description, actions, badge }: {
  title: ReactNode; description?: ReactNode; actions?: ReactNode; badge?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {badge}
        </div>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Tarjeta de indicador. Con `to` se convierte en un enlace al detalle de eso mismo: el panel
 * no es un adorno, se toca y lleva a los pedidos, los kits o el stock que está contando.
 */
export function StatCard({ label, value, hint, icon: Icon, tone = 'primary', to }: {
  label: string; value: ReactNode; hint?: ReactNode; icon: ComponentType<{ className?: string }>;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'accent' | 'neutral';
  to?: string;
}) {
  const tones: Record<string, string> = {
    primary: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
    success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
    warning: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
    danger: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
    accent: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400',
    neutral: 'bg-muted text-muted-foreground'
  };
  const contenido = (
    <>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
        {hint ? <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="flex shrink-0 items-start gap-1">
        <div className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', tones[tone])}>
          <Icon className="size-4" />
        </div>
        {to ? <ArrowUpRight className="mt-1 size-3.5 text-muted-foreground/60" aria-hidden /> : null}
      </div>
    </>
  );

  if (!to) return <div className="panel flex items-start justify-between gap-3 p-4">{contenido}</div>;
  return (
    <Link
      to={to}
      className="panel group flex items-start justify-between gap-3 p-4 transition hover:border-primary/40 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {contenido}
    </Link>
  );
}

export function EmptyState({ title, description, action, icon: Icon = Inbox }: {
  title: string; description?: string; action?: ReactNode; icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-14 text-center">
      <div className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-5" />
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? <p className="max-w-sm text-xs text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      <AlertTriangle className="size-6 text-destructive" />
      <p className="text-sm font-medium text-foreground">No se han podido cargar los datos</p>
      <p className="max-w-md text-xs text-muted-foreground">{message}</p>
      {onRetry ? <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>Reintentar</Button> : null}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Buscar…', className }: {
  value: string; onChange: (value: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="pl-8" />
    </div>
  );
}

export function ProgressBar({ value, tone = 'primary' }: { value: number; tone?: 'primary' | 'success' | 'warning' }) {
  const colors = { primary: 'bg-primary', success: 'bg-success', warning: 'bg-warning' };
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className={cn('h-full rounded-full transition-all', colors[tone])} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

export function DetailRow({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 py-1.5 text-xs', className)}>
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}
